import { SampleType } from '@resistapp/common/sampling-point-types';
import { ProjectType, SampleStatus } from '@resistapp/common/types';
import { theme } from './theme';

export function getStatusStyles(statusOrType: ProjectType | SampleStatus | string) {
  return statusOrType === (SampleStatus.APPROVED as string)
    ? { bg: 'green', text: 'white', borderRadius: theme.borders.radius.full }
    : statusOrType === (SampleStatus.UNAPPROVED as string)
      ? { bg: theme.colors.neutral200, text: theme.colors.neutral900, borderRadius: theme.borders.radius.full }
      : statusOrType === (SampleStatus.SUBMITTED as string)
        ? { bg: theme.colors.neutral500, text: 'white', borderRadius: theme.borders.radius.full }
        : statusOrType === (SampleStatus.APPROVED_WITH_ISSUES as string) ||
            statusOrType === (SampleStatus.APPROVED_SKIP_RESULT_STATISTICS as string)
          ? { bg: 'orange', text: theme.colors.neutral900, borderRadius: theme.borders.radius.full }
          : statusOrType === (SampleStatus.FAILED as string)
            ? { bg: 'red', text: theme.colors.neutral900, borderRadius: theme.borders.radius.full }
            : {
                bg: theme.colors.neutral100,
                text: theme.colors.neutral900,
                borderRadius: theme.borders.radius.full,
              };
}

export const dotStylesForSelect = (content: string, numberValue?: number | undefined) => ({
  position: 'relative',
  alignItems: 'center',
  display: 'flex',

  ':before':
    content === 'PO'
      ? {
          backgroundColor: theme.colors.neutral100,
          color: theme.colors.neutral900,
          borderRadius: theme.borders.radius.full,
          fontSize: theme.fontSize.small,
          content: `"${content}"`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginRight: theme.spacing[2],
          height: 6,
          minHeight: 6,
          width: 7,
          minWidth: 7,
          border: `1px solid ${theme.colors.neutral900}`,
        }
      : undefined,
  ':after':
    numberValue && content !== 'PO'
      ? {
          backgroundColor: theme.colors.neutral100,
          color: theme.colors.neutral900,
          borderRadius: theme.borders.radius.full,
          fontSize: theme.fontSize.small,
          content: `"${numberValue}"`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          left: theme.spacing[12],
          top: '50%',
          transform: 'translateY(-50%)',
          height: 4,
          minHeight: 4,
          width: 4,
          minWidth: 4,
          border: `1px solid ${theme.colors.neutral900}`,
          marginLeft: theme.spacing[2],
        }
      : undefined,
});

// Centralised badge styles for SampleType
const isSampleType = (v: unknown): v is SampleType =>
  typeof v === 'string' && (Object.values(SampleType) as string[]).includes(v);

const envTypeBadgeMap: Record<SampleType, { bg: string; color: string }> = {
  [SampleType.WASTEWATER]: { bg: theme.colors.blue800, color: 'white' },
  [SampleType.SURFACE_WATER]: { bg: theme.colors.blue600, color: 'white' },
  [SampleType.OTHER_WATER]: { bg: theme.colors.blue300, color: theme.colors.neutral900 },
  [SampleType.SEDIMENT]: { bg: '#b08968', color: 'white' }, // earthy brown
  [SampleType.SLUDGE]: { bg: theme.colors.maroon, color: 'white' },
  [SampleType.SOIL]: { bg: '#a06b38', color: 'white' }, // soil brown
  [SampleType.STOOL]: { bg: theme.colors.red700, color: 'white' },
  [SampleType.MANURE]: { bg: '#8B4513', color: 'white' }, // saddle brown
  [SampleType.FOOD]: { bg: theme.colors.green700, color: 'white' },
  [SampleType.CONTROL]: { bg: theme.colors.neutral600, color: 'white' },
  [SampleType.OTHER]: { bg: theme.colors.neutral300, color: theme.colors.neutral900 },
};

export function getSampleTypeBadgeStyles(envType: SampleType | string): { bg: string; color: string } {
  return isSampleType(envType)
    ? envTypeBadgeMap[envType]
    : { bg: theme.colors.neutral200, color: theme.colors.neutral900 };
}

// Prefer this for pleasant light badges via Chakra's subtle variant
const envTypeColorSchemeMap: Record<SampleType, string> = {
  [SampleType.WASTEWATER]: 'blue',
  [SampleType.SURFACE_WATER]: 'blue',
  [SampleType.OTHER_WATER]: 'blue',
  [SampleType.SEDIMENT]: 'orange',
  [SampleType.SLUDGE]: 'purple',
  [SampleType.SOIL]: 'yellow',
  [SampleType.STOOL]: 'red',
  [SampleType.MANURE]: 'orange',
  [SampleType.FOOD]: 'green',
  [SampleType.CONTROL]: 'gray',
  [SampleType.OTHER]: 'gray',
};

export function getSampleTypeColorScheme(envType: SampleType | string): string {
  return isSampleType(envType) ? envTypeColorSchemeMap[envType] : 'gray';
}
